/* ProvenanceBadge (C2PA "CR", rule 37, spec 6.16) and MemoryChip (rule 23, spec 6.12). */
import { useEffect, useId, useRef, useState } from 'react';
import { Brain } from 'lucide-react';
import { Button } from './basics';

export interface Provenance { madeBy?: string; model?: string; when?: string; edits?: string[] }

export function ProvenanceBadge({ name, info }: { name: string; info: Provenance }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  useClickAway(ref, open, () => setOpen(false));
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }} data-cap="SAFE-05">
      <button type="button" className="cr-btn" aria-label={`Content credentials for ${name}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>CR</button>
      {open && (
        <span id={id} role="dialog" aria-label="Content credentials" className="prov-pop" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          <b>Content credentials</b>
          <span>Made by {info.madeBy ?? 'Crosspoint AI +'}{info.model ? ` with ${info.model}` : ''}{info.when ? `, ${info.when}` : ''}.</span>
          {info.edits?.length ? <span className="small muted">Edits: {info.edits.join(', ')}</span> : null}
          <span className="small muted">What is this? The CR mark means a record of how this was made travels with the file. It does not mean the content is true.</span>
        </span>
      )}
    </span>
  );
}

export interface MemoryItem { id: string; text: string }

export function MemoryChip({ items, onForget, onEdit, onDisableForChat }: {
  items: MemoryItem[];
  onForget?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDisableForChat?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();
  useClickAway(ref, open, () => setOpen(false));
  if (!items.length) return null;
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }} data-cap="MEM-01">
      <button type="button" className="memchip" aria-haspopup="dialog" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        <Brain size={14} aria-hidden="true" /> Used {items.length} memor{items.length === 1 ? 'y' : 'ies'}
      </button>
      {open && (
        <div id={id} role="dialog" aria-label="Memories used in this reply" className="mem-pop" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          {items.map((m) => (
            <div key={m.id} className="memrow">
              <span>{m.text}</span>
              <span className="row">
                {onEdit && <Button size="sm" variant="ghost" onClick={() => onEdit(m.id)}>Edit</Button>}
                {onForget && <Button size="sm" variant="danger" onClick={() => onForget(m.id)}>Forget</Button>}
                {onDisableForChat && <Button size="sm" variant="ghost" onClick={() => onDisableForChat(m.id)}>Don't use in this chat</Button>}
              </span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

function useClickAway(ref: React.RefObject<HTMLElement | null>, active: boolean, onAway: () => void) {
  const cb = useRef(onAway);
  cb.current = onAway;
  useEffect(() => {
    if (!active) return;
    const h = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) cb.current(); };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, [active, ref]);
}
